import React, { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, Globe } from "lucide-react";
import { useLang } from "../../context/LangContext";
import { COMPANY } from "../../lib/siteData";

const navLinks = [
  { to: "/", key: "home" },
  { to: "/about", key: "about" },
  { to: "/services", key: "services" },
  { to: "/industries", key: "industries" },
  { to: "/partners", key: "partners" },
  { to: "/projects", key: "projects" },
  { to: "/contact", key: "contact" },
];

const Header = () => {
  const { t, lang, setLang } = useLang();
  const [open, setOpen] = useState(false);

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-steel-200"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex items-center justify-between h-20 lg:h-24">
          <Link to="/" className="flex items-center gap-3" data-testid="header-logo-link">
            <img
              src={COMPANY.logos.noBuffer}
              alt="Waves Technology FZCO"
              className="h-14 sm:h-16 lg:h-20 w-auto object-contain"
            />
          </Link>

          <nav className="hidden lg:flex items-center gap-5 xl:gap-7">
            {navLinks.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`nav-${l.key}`}
                className={({ isActive }) =>
                  `text-[12px] xl:text-[13px] uppercase tracking-[0.16em] font-medium transition-colors whitespace-nowrap ${
                    isActive ? "text-brand" : "text-ink-soft hover:text-brand"
                  }`
                }
              >
                {l.key === "services" ? (lang === "ar" ? t("nav.services") : "Services") : t(`nav.${l.key}`)}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <button
              data-testid="lang-toggle"
              onClick={() => setLang(lang === "en" ? "ar" : "en")}
              className="hidden sm:inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-ink-soft hover:text-brand transition-colors px-3 py-2 border border-steel-300 hover:border-brand"
              aria-label="Toggle language"
            >
              <Globe className="w-3.5 h-3.5" />
              {lang === "en" ? "AR" : "EN"}
            </button>
            <Link
              to="/rfq"
              data-testid="header-rfq-cta"
              className="hidden md:inline-flex bg-brand text-white hover:bg-brand-hover transition-colors font-semibold text-sm px-5 py-3 uppercase tracking-wider"
            >
              {t("nav.rfq")}
            </Link>
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen((v) => !v)}
              className="lg:hidden p-2 text-ink"
              aria-label="Toggle menu"
            >
              {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-steel-200 bg-white" data-testid="mobile-menu">
          <div className="px-6 py-6 flex flex-col gap-1">
            {navLinks.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `py-3 text-sm uppercase tracking-[0.18em] font-medium border-b border-steel-100 ${
                    isActive ? "text-brand" : "text-ink-soft"
                  }`
                }
              >
                {t(`nav.${l.key}`)}
              </NavLink>
            ))}
            <Link
              to="/rfq"
              onClick={() => setOpen(false)}
              className="mt-4 bg-brand text-white text-center py-3 font-semibold uppercase tracking-wider text-sm"
              data-testid="mobile-rfq-cta"
            >
              {t("nav.rfq")}
            </Link>
            <button
              onClick={() => setLang(lang === "en" ? "ar" : "en")}
              className="mt-3 text-xs font-semibold uppercase tracking-wider text-ink-soft border border-steel-300 py-2"
              data-testid="mobile-lang-toggle"
            >
              {lang === "en" ? "العربية" : "English"}
            </button>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
